import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import {
  AdminLabelsApi,
  type CreateLabelRequestDto,
  type LabelDto,
  LabelsApi,
  type UpdateLabelRequestDto,
} from "mws-catalog-ts-sdk";
import { useToast } from "vue-toastification";

import type { Guid, UseQueryOptionsOmitQueryKey } from "@/common/types";

const catalogApiUrl = import.meta.env.VITE_CATALOG_API;
const adminLabelApi = new AdminLabelsApi(undefined, catalogApiUrl);
const labelApi = new LabelsApi(undefined, catalogApiUrl);
export type UpdateOrCreateLabelRequest = {
  text: string;
  description: string;
};

export const useGetAllLabelsQuery = (
  options?: UseQueryOptionsOmitQueryKey<LabelDto[]>,
) => {
  const queryKey = ["labels"];

  const queryFn = async () => {
    const response = await labelApi.labelsGet();

    return response.data;
  };

  return useQuery({
    queryKey,
    queryFn,
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const useGetLabelByIdQuery = (
  id: Guid,
  options?: UseQueryOptionsOmitQueryKey<LabelDto>,
) => {
  const queryKey = ["labels", id];
  const queryFn = async () => {
    const response = await adminLabelApi.adminLabelsLabelIdGet({
      labelId: id,
    });

    return response.data;
  };

  return useQuery({
    queryKey,
    queryFn,
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const useCreateLabelMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = async (request: CreateLabelRequestDto) => {
    const response = await adminLabelApi.adminLabelsPost({
      createLabelRequestDto: request,
    });

    return response.data;
  };

  return useMutation({
    mutationFn: mutationFn,

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
};

export const useUpdateLabelMutation = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({
    request,
    labelId,
  }: {
    request: UpdateLabelRequestDto;
    labelId: Guid;
  }) => {
    const response = await adminLabelApi.adminLabelsLabelIdPatch({
      labelId,
      updateLabelRequestDto: request,
    });

    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
    onError: () => {
      toast.error("Error updating label");
    },
  });
};
